/**
 * Per-claim-type provider validation — shared by the portal's submit and edit
 * claim forms so both apply an identical rule set.
 *
 * Composes two rules, reported in this order:
 *  1. Surgeon role conflict (spec 028 FR-027) — see `getSurgeonConflictError`.
 *  2. Duplicate provider — one person cannot appear twice on the same claim,
 *     whether under two roles (e.g. both surgeon and co-surgeon) or twice under
 *     one role. The single-`providerId`-per-claim check covers both, which is
 *     why it still holds now that the anesthesia roles accept several providers
 *     each (feature 044, FR-009).
 *
 * Duplicates across *different* claim types on the same encounter stay valid:
 * a provider may legitimately be billed under more than one claim type.
 *
 * Returns a human-readable error string for the first violation found, or
 * `null` when the claim's provider assignments are valid.
 */

import { getSurgeonConflictError } from './surgeonRoleConflict';

/**
 * Structural shape of one provider assignment — matches the `ProviderAssignment`
 * domain type and the zod-inferred assignment in the portal's form schemas.
 */
interface Assignment {
  providerId: string;
  providerName: string;
  role: string;
}

export function getProviderAssignmentError(providers: readonly Assignment[]): string | null {
  const roleConflict = getSurgeonConflictError(providers.map((p) => p.role));
  if (roleConflict) return roleConflict;

  const seen = new Set<string>();
  for (const { providerId, providerName } of providers) {
    // Empty ids are caught by the per-field `min(1)` check, not here.
    if (!providerId) continue;
    if (seen.has(providerId)) {
      return `${providerName || 'This provider'} is assigned more than once on this claim. Each assignment must be a different provider.`;
    }
    seen.add(providerId);
  }

  return null;
}
